import { CSSProperties, HTMLInputTypeAttribute, InputHTMLAttributes, ReactNode, SyntheticEvent } from "react";
import { Css } from "~generated/css";
import { useTestIds } from "~utils";

// Input types that are supported via a text input.
type TextInputType = Extract<HTMLInputTypeAttribute, "email" | "password" | "search" | "tel" | "text" | "number">;

// A TextField can either be disabled, readOnly, or neither.
type DisabledTextField = {
  disabled?: boolean;
};
type ReadOnlyTextField = {
  readOnly?: boolean;
};
export type TextFieldProps = Pick<
  InputHTMLAttributes<HTMLInputElement>,
  "autoComplete" | "placeholder" | "required" | "inputMode"
> & {
  label?: ReactNode;
  onFocus?: (e: SyntheticEvent) => void;
  onBlur?: (e: SyntheticEvent) => void;
  onChange?: (e: SyntheticEvent, value: string) => void;
  onKeyDown?: (e: SyntheticEvent, key: string) => void;
  /** @default "text" */
  type?: TextInputType;
  value?: string | number;
  inputId?: string;
  startAdornment?: ReactNode;
  cssOverrides?: CSSProperties;
} & (DisabledTextField | ReadOnlyTextField);

export function TextField(props: TextFieldProps) {
  const {
    autoComplete,
    label,
    onBlur,
    onChange,
    onKeyDown,
    placeholder,
    required,
    type = "text",
    value = "",
    inputId = "text-input",
    cssOverrides,
    startAdornment,
    ...inputOthers
  } = props;
  const disabled = "disabled" in props ? (props.disabled ?? false) : false;
  const readOnly = "readOnly" in props ? (props.readOnly ?? false) : false;
  const tid = useTestIds(props, "textField");

  const customLabel =
    label && typeof label === "string" ? (
      <span {...tid.label} css={Css.t10.mbPx(4).db.if(disabled).gray700.$}>
        {label}
      </span>
    ) : (
      label
    );

  return (
    <label>
      {/* Label */}
      {label && customLabel}

      {/* Input */}
      <div css={Css.relative.df.aic.oh.br4.$}>
        {startAdornment && <div css={Css.absolute.leftPx(12).df.aic.$}>{startAdornment}</div>}
        <input
          id={inputId}
          css={{
            ...Css.bgGray50.br4.px2.py1.bshBasic.t14.w100.outline0.ifMobileOrTablet.t16.else
              .if(disabled)
              .gray700.cursorNotAllowed.if(readOnly).bgTransparent.bshNone.pl0.$,
            ...(cssOverrides ? cssOverrides : {}),
          }}
          disabled={disabled}
          value={value}
          onBlur={(e) => onBlur?.(e)}
          onChange={(e) => onChange?.(e, e.target.value)}
          onKeyDown={(e) => onKeyDown?.(e, e.key)}
          placeholder={placeholder}
          readOnly={readOnly}
          required={required}
          type={type}
          autoComplete={autoComplete}
          {...inputOthers}
          {...tid.input}
        />
      </div>
    </label>
  );
}

// TODO: This will become the new standard for text fields soon-ish
//   until we have all the states and variants from design we'll export overrides
// FIXME: It is 2025, we're on the 3rd iteration of this checkout flow. lets just make this the norm
export const NewFieldLabel = (title: string, noMb = false) => (
  <div css={Css.t14.fw500.gray900.ttn.if(!noMb).mb1.$}>{title}</div>
);
export const NewFieldStyleOverrides = Css.bshNone
  .bc("rgba(53, 53, 53, 0.16)")
  .ba.br8.p2.hPx(48)
  .addIn(":disabled", Css.bgGray200.$).$;
